import { z } from 'zod';
import { PageQuerySchema, UuidSchema } from './common';
import { EmployeeStatusSchema, RoleSchema } from './enums';

/** §13 ТЗ, раздел «Пользователи и роли»: сотрудники, роли, блокировка, сброс пароля. */

export const EmployeeSummarySchema = z.object({
  id: UuidSchema,
  fullName: z.string(),
  email: z.string().email(),
  role: RoleSchema,
  status: EmployeeStatusSchema,
  /** §15.1: сопоставление с пользователем портала; null в автономном режиме */
  bitrixUserId: z.number().int().nullable(),
  mustChangePassword: z.boolean(),
  /** у входящих через портал пароля нет — напоминание о его смене им не показывается */
  hasPassword: z.boolean(),
  lastLoginAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  /** объекты, где сотрудник назначен менеджером: блокировка назначение не снимает */
  managedProjectCount: z.number().int().min(0),
});
export type EmployeeSummary = z.infer<typeof EmployeeSummarySchema>;

export const ListEmployeesQuerySchema = PageQuerySchema.extend({
  role: RoleSchema.optional(),
  status: EmployeeStatusSchema.optional(),
});
export type ListEmployeesQuery = z.infer<typeof ListEmployeesQuerySchema>;

/**
 * §15.3: начальный пароль задаёт сисадмин и передаёт сотруднику сам;
 * смена при первом входе обязательна, поэтому пароль долго не живёт.
 *
 * §15.1: во встроенном режиме вход идёт через портал, и пароль сотруднику не
 * нужен вовсе — `passwordHash` в схеме не зря допускает `null`. Печь пароль,
 * который никто никогда не введёт, значит без нужды расширять поверхность §18.
 */
export const CreateEmployeeRequestSchema = z.object({
  fullName: z.string().trim().min(3).max(200),
  email: z.string().email().max(320),
  role: RoleSchema,
  /** §15.1: пользователь портала; уникален — один пользователь на сотрудника */
  bitrixUserId: z.number().int().positive().optional(),
  /**
   * Нужен ли вход по паролю. Не задано — решает сервер по режиму работы:
   * во встроенном режиме пароль не заводится, в остальных заводится всегда.
   */
  localLogin: z.boolean().optional(),
  /**
   * Свой пароль вместо сгенерированного. §18: не короче 12 символов.
   * Обычный путь — генерация на сервере: она даёт все четыре класса символов
   * и не проходит через голову сотрудника, который заводит учётную запись.
   */
  initialPassword: z.string().min(12).max(200).optional(),
});
export type CreateEmployeeRequest = z.infer<typeof CreateEmployeeRequestSchema>;

/**
 * §18: сгенерированный пароль возвращается ровно один раз — в ответе на
 * создание. В базе он лежит только хешем, повторно показать его нельзя,
 * можно лишь выпустить новый сбросом (тот же порядок, что у PIN приглашения).
 */
export const EmployeeSecretResultSchema = z.object({
  employee: EmployeeSummarySchema,
  /** null — пароля нет: вход только через портал (§15.1) или задан свой */
  password: z.string().nullable(),
});
export type EmployeeSecretResult = z.infer<typeof EmployeeSecretResultSchema>;

/** §15.1: привязка сотрудника к пользователю портала; `null` снимает её. */
export const LinkBitrixUserRequestSchema = z.object({
  bitrixUserId: z.number().int().positive().nullable(),
});
export type LinkBitrixUserRequest = z.infer<typeof LinkBitrixUserRequestSchema>;

/** Пользователь портала в диалоге сопоставления (§15.1, §16.2 — `user.get`). */
export const B24UserOptionSchema = z.object({
  bitrixUserId: z.number().int(),
  fullName: z.string(),
  email: z.string().nullable(),
  position: z.string().nullable(),
  /** уже сопоставленный сотрудник — повторная привязка отклоняется */
  linkedEmployeeId: UuidSchema.nullable(),
});
export type B24UserOption = z.infer<typeof B24UserOptionSchema>;

export const ChangeRoleRequestSchema = z.object({ role: RoleSchema });
export type ChangeRoleRequest = z.infer<typeof ChangeRoleRequestSchema>;

/**
 * Сброс: новый временный пароль + принудительная смена при входе (§15.3).
 * Пароль не передан — сервер генерирует его сам и показывает один раз.
 */
export const ResetPasswordRequestSchema = z.object({
  newPassword: z.string().min(12).max(200).optional(),
});
export type ResetPasswordRequest = z.infer<typeof ResetPasswordRequestSchema>;
